/**
 * useNarration — Audio playback hook for document narration.
 *
 * Fetches narrated audio from /api/v1/narrate and manages playback state
 * including play/pause, progress tracking, and paragraph sync.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface UseNarrationResult {
  /** Whether narration is currently loading */
  isLoading: boolean;
  /** Whether narration audio is playing */
  isPlaying: boolean;
  /** Playback progress 0-1 */
  progress: number;
  /** Duration in seconds (available after loading) */
  duration: number;
  /** Start narration for a document */
  startNarration: (documentId: string, projectId: string) => Promise<void>;
  /** Toggle play/pause */
  togglePlayback: () => void;
  /** Stop narration and reset */
  stopNarration: () => void;
  /** Error message if narration failed */
  error: string | null;
}

export function useNarration(): UseNarrationResult {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animFrameRef = useRef<number>(0);

  // Progress tracking
  const updateProgress = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.duration && !isNaN(audio.duration)) {
      setProgress(audio.currentTime / audio.duration);
      setDuration(audio.duration);
    }
    if (!audio.paused) {
      animFrameRef.current = requestAnimationFrame(updateProgress);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (audioRef.current) {
        audioRef.current.pause();
        const src = audioRef.current.src;
        audioRef.current = null;
        if (src.startsWith('blob:')) URL.revokeObjectURL(src);
      }
    };
  }, []);

  const startNarration = useCallback(async (documentId: string, projectId: string) => {
    setIsLoading(true);
    setError(null);
    setProgress(0);

    try {
      const response = await fetch('/api/v1/narrate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_id: documentId, project_id: projectId }),
        credentials: 'include',
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Narration failed' }));
        throw new Error(err.error || `Narration failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Stop any current audio
      if (audioRef.current) {
        audioRef.current.pause();
        const oldSrc = audioRef.current.src;
        if (oldSrc.startsWith('blob:')) URL.revokeObjectURL(oldSrc);
      }

      const audio = new Audio(url);
      audioRef.current = audio;

      audio.onloadedmetadata = () => {
        setDuration(audio.duration);
      };

      audio.onended = () => {
        setIsPlaying(false);
        setProgress(1);
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      };

      audio.onerror = () => {
        setError('Audio playback error');
        setIsPlaying(false);
      };

      audio.onplay = () => {
        setIsPlaying(true);
        animFrameRef.current = requestAnimationFrame(updateProgress);
      };

      audio.onpause = () => {
        setIsPlaying(false);
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      };

      await audio.play();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [updateProgress]);

  const togglePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  }, []);

  const stopNarration = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      const src = audioRef.current.src;
      audioRef.current = null;
      if (src.startsWith('blob:')) URL.revokeObjectURL(src);
    }
    setIsPlaying(false);
    setProgress(0);
    setDuration(0);
    setError(null);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
  }, []);

  return {
    isLoading,
    isPlaying,
    progress,
    duration,
    startNarration,
    togglePlayback,
    stopNarration,
    error,
  };
}
